// Assemblies — shared helpers for the Child Part Tracker (plans/bom-assemblies.md).
// Pure functions only: no React, no network. Mirrors the backend's
// _bom_summary / derived-status rules so rows, pills and gates agree.

export const isAssembly = (item) => item?.build_type === "assembly";

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// "2026-10-24" → "24-Oct-26" (the format the factory already uses in its sheets)
export function fmtBomDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.slice(0, 10).split("-");
  if (!y || !m || !d) return iso;
  return `${d}-${MON[Number(m) - 1] || m}-${y.slice(2)}`;
}

export function daysFromToday(iso) {
  if (!iso) return null;
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  const t = new Date(); t.setHours(0, 0, 0, 0);
  return Math.round((new Date(y, m - 1, d) - t) / 86400000);
}

export const isLate = (p) => p?.status === "Ordered" && !!p.eta && daysFromToday(p.eta) < 0;

export function bomSummary(bom) {
  const parts = bom || [];
  const n = parts.length;
  const received = parts.filter(p => p.status === "Received").length;
  const ordered = parts.filter(p => p.status === "Ordered").length;
  const late = parts.filter(isLate).length;
  let state = "pending";
  if (n && received === n) state = "complete";
  else if (late) state = "overdue";
  else if (received) state = "partial";
  else if (ordered) state = "ordered";
  return { n, received, ordered, late, notOrdered: n - received - ordered, outstanding: n - received, state };
}

// Tone of one part's ETA cell: none (not ordered) | ok | soon (≤3 days) | late | recd
export function etaTone(p) {
  if (p.status === "Received") return "recd";
  if (p.status !== "Ordered" || !p.eta) return "none";
  const d = daysFromToday(p.eta);
  if (d < 0) return "late";
  if (d <= 3) return "soon";
  return "ok";
}

export function etaSubLabel(p) {
  if (p.status === "Received") return "Received";
  if (p.status !== "Ordered" || !p.eta) return "";
  const d = daysFromToday(p.eta);
  if (d < 0) return `${-d} day${d === -1 ? "" : "s"} late`;
  if (d === 0) return "Due today";
  if (d <= 3) return `Due in ${d} day${d === 1 ? "" : "s"}`;
  return "";
}

export const MTC_META = {
  approved:     { label: "Approved",     cls: "text-emerald-700 dark:text-emerald-400 bg-emerald-500/10 border-emerald-500/30" },
  not_approved: { label: "Not Approved", cls: "text-red-700 dark:text-red-400 bg-red-500/10 border-red-500/30" },
  pending:      { label: "Not Approved", cls: "text-red-700 dark:text-red-400 bg-red-500/10 border-red-500/30" },
  not_required: { label: "Not required", cls: "text-[hsl(var(--muted-foreground))] bg-[hsl(var(--muted))]/60 border-[hsl(var(--border))]" },
};
// Click cycles Not approved → Approved → Not required → Not approved.
export const MTC_NEXT = { pending: "approved", not_approved: "approved", approved: "not_required", not_required: "not_approved" };

export function fmtMoney(v, cur) {
  const n = Number(v || 0);
  return cur === "USD"
    ? "$" + n.toLocaleString("en-US", { maximumFractionDigits: 2 })
    : "₹" + n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

// The pill / button palette shared by every assembly surface. Same hues as the
// procurement status pills (slate · blue · amber · emerald) plus red for late.
export const BOM_STATE_CLS = {
  // Solid on purpose: dashed + breathing is reserved for the EMPTY list.
  pending:  "rl-pill border-slate-400/45 bg-slate-500/5 text-slate-700 dark:text-slate-300 hover:bg-slate-500/10",
  ordered:  "rl-pill border-blue-500/30 bg-blue-500/5 text-blue-700 dark:text-blue-400 hover:bg-blue-500/10",
  partial:  "rl-pill border-amber-500/40 bg-amber-500/5 text-amber-700 dark:text-amber-400 hover:bg-amber-500/10",
  overdue:  "rl-pill border-red-500/35 bg-red-500/5 text-red-700 dark:text-red-400 hover:bg-red-500/10",
  complete: "rl-pill border-emerald-500/35 bg-emerald-500/5 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/10",
};
export const BOM_STATE_TITLE = {
  pending: "Parts list ready — nothing ordered yet",
  ordered: "Parts on order, none late",
  partial: "Some parts received, the rest on time",
  overdue: "At least one part is past its ETA",
  complete: "Every child part received",
};

/* ── Paste parser ────────────────────────────────────────────────────────
 * One part per line. Columns: tab-separated (an Excel paste) when tabs are
 * present; otherwise the first word is the part number IF it looks like one.
 * It never splits on commas unless the user explicitly picks "Comma", because
 * real descriptions ("Body, Reamer, dbk, valve…") are full of them.
 * Returns rows: { skip, why, raw } or { part_no, description, qty, flags[] }
 * where a flag is ["error"|"warn", message]. */
const looksLikePartNo = (s) => /^[A-Z0-9][A-Z0-9\-/.]{2,}$/i.test(s) && /\d/.test(s);
const isNum = (s) => /^\d+(\.\d+)?$/.test(String(s).trim());

export function parsePartsPaste(text, sep = "auto", existing = []) {
  const rows = [];
  (text || "").split(/\r?\n/).forEach((raw) => {
    const line = raw.trim();
    if (!line) { if (raw.length || rows.length) rows.push({ skip: true, why: "Blank line — skipped", raw }); return; }
    let cols;
    if (sep === "tab" || (sep === "auto" && raw.includes("\t"))) cols = raw.split("\t").map(s => s.trim());
    else if (sep === "comma") cols = line.split(",").map(s => s.trim());
    else {
      const m = line.match(/^(\S+)\s+(.*)$/);
      cols = m && looksLikePartNo(m[1]) ? [m[1], m[2]] : ["", line];
      // trailing "× 4" / "x 4" / "qty 4" → qty
      const q = cols[1].match(/^(.*?)[\s,]+(?:x|×|qty[:\s]*)\s*(\d+(?:\.\d+)?)\s*$/i);
      if (q) cols = [cols[0], q[1].trim(), q[2]];
    }
    let [part_no = "", description = "", qty = ""] = cols;
    // "Description<TAB>4" (no part-no column)
    if (cols.length === 2 && !looksLikePartNo(part_no) && isNum(description)) { qty = description; description = part_no; part_no = ""; }
    if (/^(child\s*)?part\s*(no|number|#)/i.test(part_no) || /^description$/i.test(description)) {
      rows.push({ skip: true, why: "Header row — skipped", raw }); return;
    }
    const r = { part_no, description, qty: qty === "" ? 1 : qty, flags: [] };
    if (sep === "comma" && cols.length > 3) r.flags.push(["error", `Split into ${cols.length} pieces — this description has commas. Switch back to Auto.`]);
    if (!description) r.flags.push(["error", "Description missing"]);
    if (qty !== "" && (!isNum(qty) || Number(qty) <= 0)) r.flags.push(["error", `Qty "${qty}" isn't a number`]);
    if (!part_no) r.flags.push(["warn", "No part number — fine for consumables"]);
    rows.push(r);
  });
  // drop trailing blank-line rows (an artefact of pasting with a final newline)
  while (rows.length && rows[rows.length - 1].skip && !rows[rows.length - 1].raw.trim()) rows.pop();
  // duplicates — within the paste and against parts already on the list
  const seen = {};
  const have = new Set((existing || []).map(p => (p.part_no || "").toUpperCase()).filter(Boolean));
  let n = 0;
  rows.forEach(r => {
    if (r.skip) return;
    n += 1;
    const key = (r.part_no || "").toUpperCase();
    if (!key) return;
    if (have.has(key)) r.flags.push(["warn", "Already on this assembly's list"]);
    else if (seen[key]) r.flags.push(["warn", `Same part number as row ${seen[key]} — keep both only if intended`]);
    else seen[key] = n;
  });
  return rows;
}
